import { ratingTone } from "./theme";
import LearnLink from "./LearnLink";

/**
 * Body-level heading components used throughout the report:
 *   - ReportHeader: company name + period meta + risk-rating badge.
 *   - SectionTitle: eyebrow + headline pair used above each section grid.
 *     Optionally accepts `learnSlug` to render an inline "Read in 30s →"
 *     CTA on the right — turns each section heading into a hop into the
 *     glossary explainer for that domain (profitability, liquidity, etc.).
 */

export function ReportHeader({ input, health }) {
  const tone = ratingTone(health.rating);
  return (
    <div
      className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up"
      data-testid="report-header"
    >
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">CFO Grade report</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-none mt-3 tracking-tight">
          {input.company_name || "Your Business"}
        </h1>
        <p className="text-[#5C665D] mt-2">
          {input.period || "Latest period"} · {input.industry || "—"} · {input.currency || "USD"}
        </p>
      </div>
      <span
        className={`self-start sm:self-auto inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium ${tone.bg} ${tone.text}`}
        data-testid="health-rating-badge"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {health.rating || "—"} · {health.risk_level || "—"} risk
      </span>
    </div>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  learnSlug,
  learnLabel,
  proOnly = false,
  sampleMode = false,
  className = "",
}) {
  // Pro-only badge surfaces ONLY in sample mode so prospective buyers can see
  // exactly which slices of the report unlock with a purchase. Paying users
  // already have everything — no need to nag them with paywall markers.
  const showProBadge = proOnly && sampleMode;
  return (
    <div className={`mt-14 mb-6 ${className}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D] flex items-center gap-2">
            {eyebrow}
            {showProBadge && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#9c6b1d] bg-[#9c6b1d]/10 border border-[#9c6b1d]/30 rounded-full px-2 py-0.5 normal-case font-medium"
                data-testid={`pro-badge-${(eyebrow || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                aria-label="Available in the paid version"
                title="This section is part of CFO Grade Pro — included when you purchase a Pass or PDF unlock."
              >
                <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8Z" />
                </svg>
                In the paid report
              </span>
            )}
          </p>
          <h2 className="font-serif-display text-3xl text-[#1F382E] mt-2 tracking-tight">{title}</h2>
        </div>
        {learnSlug && (
          <LearnLink
            slug={learnSlug}
            label={learnLabel || "Read this in 30s"}
            source={`section_${eyebrow.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`}
            variant="cta"
            className="mb-1"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Inline pill marker placed directly above a card body to label individual
 * features as paid or free. Only intended to be rendered when `sampleMode` is
 * true on the caller — paying users never see these.
 *
 *   <SectionMarker kind="paid" />   →  ★ In the paid report (gold)
 *   <SectionMarker kind="free" />   →  ● Always free       (green)
 *
 * Keeping the visual deliberately small + low-contrast so the markers read
 * as informational labels, not paywall nags.
 */
export function SectionMarker({ kind = "paid", className = "" }) {
  const isPaid = kind === "paid";
  return (
    <div
      className={`flex items-center mb-3 ${className} no-print`}
      data-testid={`section-marker-${kind}`}
    >
      <span
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 font-medium ${
          isPaid
            ? "text-[#9c6b1d] bg-[#9c6b1d]/10 border border-[#9c6b1d]/30"
            : "text-[#2C5C43] bg-[#2C5C43]/10 border border-[#2C5C43]/30"
        }`}
        aria-label={isPaid ? "Available in the paid report" : "Always free"}
      >
        {isPaid ? (
          <>
            <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8Z" />
            </svg>
            <span>In the paid report</span>
          </>
        ) : (
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-[#2C5C43]" aria-hidden="true" />
            <span>Always free</span>
          </>
        )}
      </span>
    </div>
  );
}
